import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  Scissors,
  Download,
  CheckCircle2,
  FileAudio,
  Loader2,
  X,
} from 'lucide-react';
import { extractSample, ExtractSampleResponse } from '../lib/api';
import './SampleExtractor.css';

interface SampleExtractorProps {
  analysisId: string;
  startTime: number;
  endTime: number;
  sectionType: string;
}

const formatTime = (s: number): string => {
  if (!Number.isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const ACK_KEY = 'knkt-sample-legal-ack';

export function SampleExtractor({ analysisId, startTime, endTime, sectionType }: SampleExtractorProps) {
  const [isExtracting, setIsExtracting] = useState(false);
  const [sampleUrl, setSampleUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showLegal, setShowLegal] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const duration = Math.max(0, endTime - startTime);

  const runExtract = async () => {
    setIsExtracting(true);
    setError(null);
    setSampleUrl(null);

    try {
      const result: ExtractSampleResponse = await extractSample(
        analysisId,
        startTime,
        endTime,
        sectionType,
      );
      setSampleUrl(result.sampleUrl);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to extract sample');
    } finally {
      setIsExtracting(false);
    }
  };

  const handleExtractClick = () => {
    // Skip the modal once acknowledged for the session.
    const previouslyAcked = sessionStorage.getItem(ACK_KEY) === '1';
    if (previouslyAcked) {
      runExtract();
    } else {
      setShowLegal(true);
    }
  };

  const handleConfirm = () => {
    if (!acknowledged) return;
    sessionStorage.setItem(ACK_KEY, '1');
    setShowLegal(false);
    runExtract();
  };

  const handleDownload = () => {
    if (!sampleUrl) return;
    const link = document.createElement('a');
    link.href = sampleUrl;
    link.download = `${sectionType}_${startTime.toFixed(2)}_${endTime.toFixed(2)}.wav`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Close modal on Escape
  useEffect(() => {
    if (!showLegal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowLegal(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showLegal]);

  return (
    <div className="sample-extractor">
      <div className="sample-header">
        <div className="sample-header-row">
          <span className="sample-header-icon">
            <Scissors size={14} strokeWidth={1.8} />
          </span>
          <span className="sample-header-label">Extract Sample</span>
        </div>
        <div className="sample-region">
          <span className="sample-region-cell">
            <span className="sample-region-key">From</span>
            <span className="sample-region-value">{formatTime(startTime)}</span>
          </span>
          <span className="sample-region-divider" aria-hidden="true">→</span>
          <span className="sample-region-cell">
            <span className="sample-region-key">To</span>
            <span className="sample-region-value">{formatTime(endTime)}</span>
          </span>
          <span className="sample-region-cell">
            <span className="sample-region-key">Length</span>
            <span className="sample-region-value">{formatTime(duration)}</span>
          </span>
        </div>
      </div>

      <div className="sample-actions">
        <button
          className="extract-button"
          onClick={handleExtractClick}
          disabled={isExtracting}
        >
          {isExtracting ? (
            <>
              <Loader2 size={14} strokeWidth={2.2} className="extract-spin" />
              <span>Extracting…</span>
            </>
          ) : (
            <>
              <Scissors size={14} strokeWidth={2} />
              <span>Extract sample</span>
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="sample-error" role="alert">
          <AlertTriangle size={12} strokeWidth={2} />
          <span>{error}</span>
        </div>
      )}

      {sampleUrl && (
        <div className="sample-result">
          <div className="sample-success">
            <CheckCircle2 size={14} strokeWidth={2.2} />
            <span>Sample ready</span>
          </div>
          <audio controls src={sampleUrl} className="sample-preview" />
          <div className="sample-result-actions">
            <button className="download-button" onClick={handleDownload}>
              <Download size={14} strokeWidth={2} />
              <span>Download</span>
            </button>
            <span className="sample-filename">
              <FileAudio size={11} strokeWidth={1.8} />
              {sectionType}_{startTime.toFixed(2)}_{endTime.toFixed(2)}.wav
            </span>
          </div>
        </div>
      )}

      {showLegal && createPortal(
        <div
          className="sample-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sample-legal-title"
          onClick={() => setShowLegal(false)}
        >
          <div
            className="sample-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="sample-modal-close"
              onClick={() => setShowLegal(false)}
              aria-label="Close"
            >
              <X size={14} strokeWidth={2} />
            </button>

            <span className="sample-modal-eyebrow">Before you extract</span>
            <h3 id="sample-legal-title" className="sample-modal-title">
              Legal &amp; sampling notice
            </h3>

            <div className="sample-modal-icon" aria-hidden="true">
              <AlertTriangle size={20} strokeWidth={1.8} />
            </div>

            <div className="sample-modal-body">
              <p>
                Sample extraction is intended for{' '}
                <strong>educational, research, and personal study</strong>{' '}
                purposes only. Extracted audio remains the copyright of its
                original rights holders.
              </p>
              <p>
                <strong>KNKT is not responsible</strong> for misuse,
                redistribution, or commercial release of extracted audio.{' '}
                <strong>Always clear the sample</strong> with the relevant
                rights holders (label, publisher, songwriter) before
                publishing, releasing, or monetizing any work that includes it.
              </p>
            </div>

            <label className="sample-consent">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="sample-consent-input"
              />
              <span className="sample-consent-box" aria-hidden="true">
                {acknowledged && <CheckCircle2 size={12} strokeWidth={2.4} />}
              </span>
              <span className="sample-consent-text">
                I understand and will clear samples before publishing.
              </span>
            </label>

            <div className="sample-modal-actions">
              <button
                type="button"
                className="sample-modal-cancel"
                onClick={() => setShowLegal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="extract-button"
                onClick={handleConfirm}
                disabled={!acknowledged}
              >
                <Scissors size={14} strokeWidth={2} />
                <span>Confirm &amp; extract</span>
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
